import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../services/api";

export interface TimelineEvent {
  t_ms: number;
  type: string;
  payload: Record<string, unknown>;
}

export interface UseRecordingResult {
  events: TimelineEvent[];
  videoUrl: string | null;
  loading: boolean;
  error: string | null;
}

export function useRecording(sessionId: string): UseRecordingResult {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [tlRes, vidRes] = await Promise.all([
          apiFetch(`/sessions/${sessionId}/recording/timeline`),
          apiFetch(`/sessions/${sessionId}/recording/download`),
        ]);

        if (!tlRes.ok) throw new Error(`Timeline: HTTP ${tlRes.status}`);
        if (!vidRes.ok) throw new Error(`Video: HTTP ${vidRes.status}`);

        const [tlData, vidBlob] = await Promise.all([
          tlRes.json() as Promise<TimelineEvent[]>,
          vidRes.blob(),
        ]);

        if (cancelled) return;

        const url = URL.createObjectURL(vidBlob);
        blobUrlRef.current = url;
        setEvents(tlData);
        setVideoUrl(url);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [sessionId]);

  return { events, videoUrl, loading, error };
}
